import { Button } from "@/components/common/Button/Button"
import { FocusCycleIcon } from "@/components/common/icons/FocusCircleIcon/FocusCycleIcon"
import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { URLS } from "@/utils/urls.const"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { IconArrowRight, IconX } from "@tabler/icons-react"
import React, { useLayoutEffect, useMemo, useState } from "react"

interface OnboardingStep {
  refId: string
  title: string
  description: string
  video: string
}

interface OnboardingTooltipProps {
  showTour: boolean
  handleEndTour: () => void
  handleCompleteTour: () => void
}

export const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  showTour,
  handleEndTour,
  handleCompleteTour,
}) => {
  const { translate } = useTranslation()
  const [currentStep, setCurrentStep] = useState(0)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [focusIconPosition, setFocusIconPosition] = useState({
    top: 0,
    left: 0,
  })

  const steps: OnboardingStep[] = useMemo(
    () => [
      {
        refId: "navItem1",
        title: translate("ONBOARDING/UPLOAD/TITLE"),
        description: translate("ONBOARDING/UPLOAD/DESCRIPTION"),
        video: URLS.VIDEO_URLS.UPLOAD,
      },
      {
        refId: "navItem2",
        title: translate("ONBOARDING/HISTORY/TITLE"),
        description: translate("ONBOARDING/HISTORY/DESCRIPTION"),
        video: URLS.VIDEO_URLS.QUESTIONNAIRE_OVERVIEW,
      },
      {
        refId: "navItem3",
        title: translate("ONBOARDING/KNOWLEDGEBASE/TITLE"),
        description: translate("ONBOARDING/KNOWLEDGEBASE/DESCRIPTION"),
        video: URLS.VIDEO_URLS.DATA_TUTORIAL,
      },
    ],
    [translate],
  )

  useLayoutEffect(() => {
    const step = steps[currentStep]
    const element = document.getElementById(step.refId)
    if (element) {
      setAnchorEl(element)

      // Calculate the actual position of the element
      let rect = element.getBoundingClientRect()

      // If the element is wide (likely the button container), try to find the icon/avatar inside
      // This handles cases where the ID might be on the container instead of the icon itself
      if (rect.width > 60 && element.firstElementChild) {
        const childRect = element.firstElementChild.getBoundingClientRect()
        // Ensure the child is actually visible and has dimensions
        if (childRect.width > 0 && childRect.height > 0) {
          rect = childRect
        }
      }

      // The focus icon is 44x44px, center it over the sidebar icon
      const focusIconSize = 44
      setFocusIconPosition({
        top: rect.top + (rect.height - focusIconSize) / 2,
        left: rect.left + (rect.width - focusIconSize) / 2,
      })
      return
    }

    if (showTour) {
      setAnchorEl(document.body)
    }
  }, [currentStep, steps, showTour])

  const handleNext = () => {
    setCurrentStep((prev) => (prev + 1 < steps.length ? prev + 1 : 0))
  }

  const handleClose = () => {
    setCurrentStep(0)
    handleEndTour()
  }

  const open = Boolean(anchorEl)

  return (
    <>
      {open && showTour && (
        <div className="fixed inset-0 z-50 h-screen w-screen bg-grey3 opacity-80">
          <FocusCycleIcon
            className="absolute z-50 mb-2 animate-pulse"
            style={{
              left: `${focusIconPosition.left}px`,
              top: `${focusIconPosition.top}px`,
            }}
          ></FocusCycleIcon>
          {/* Behaves like the MUI Popover this replaced: a modal card centred
              in the viewport, above the overlay, that Escape and outside
              clicks leave open — only the close button ends the tour. */}
          <DialogPrimitive.Root open={open}>
            <DialogPrimitive.Portal>
              <div className="fixed inset-0 z-1300 flex items-center justify-center">
                <DialogPrimitive.Content
                  aria-describedby={undefined}
                  onEscapeKeyDown={(event) => event.preventDefault()}
                  onInteractOutside={(event) => event.preventDefault()}
                  className="data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 max-h-[calc(100%-32px)] max-w-[calc(100%-32px)] overflow-y-auto overflow-x-hidden rounded-[10px] bg-grey3 shadow-[0px_5px_5px_-3px_rgba(0,0,0,0.2),0px_8px_10px_1px_rgba(0,0,0,0.14),0px_3px_14px_2px_rgba(0,0,0,0.12)] outline-hidden"
                >
                  <div className="max-w-3xl bg-grey3 p-4">
                    <div className="flex items-center justify-between">
                      <DialogPrimitive.Title asChild>
                        <div>
                          <Typography variant="textM-bold">
                            {steps[currentStep].title}
                          </Typography>
                        </div>
                      </DialogPrimitive.Title>
                      <button
                        className="flex items-center text-xs text-white hover:text-grey5"
                        onClick={handleClose}
                        aria-label={translate("ONBOARDING/CLOSE_ARIA")}
                      >
                        <IconX size={16} />
                      </button>
                    </div>
                    <video
                      key={steps[currentStep].video}
                      className="my-3 aspect-video w-full rounded-lg border border-grey5"
                      controls
                      autoPlay
                    >
                      <source src={steps[currentStep].video} type="video/mp4" />
                      {translate("ONBOARDING/VIDEO_UNSUPPORTED")}
                    </video>
                    <Typography variant={"textM"} className="py-2">
                      {steps[currentStep].description}
                    </Typography>
                    <div className="flex items-center justify-between pt-2">
                      <Typography variant={"textXS"} className="text-grey5">
                        {currentStep + 1} {translate("ONBOARDING/OF")}{" "}
                        {steps.length}
                      </Typography>
                      {currentStep + 1 < steps.length && (
                        <Button
                          variantColor="yellow"
                          label={translate("ONBOARDING/NEXT_ARIA")}
                          IconRight={IconArrowRight}
                          variantSize="xsmall"
                          onClick={handleNext}
                        />
                      )}
                      {currentStep + 1 === steps.length && (
                        <Button
                          variantColor="yellow"
                          title={translate("ONBOARDING/COMPLETE")}
                          label={translate("ONBOARDING/COMPLETE_ARIA")}
                          variantSize="xsmall"
                          onClick={handleCompleteTour}
                        />
                      )}
                    </div>
                  </div>
                </DialogPrimitive.Content>
              </div>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>
        </div>
      )}
    </>
  )
}
